import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AchievementId } from '../_types/achievement'
import { useImportedListings } from './use-imported-listings'
import { useListingDecisions } from './use-listing-decisions'
import { useQuickPassProfile } from './use-quickpass-profile'
import { useAuth } from './use-auth'
import { useCelebration } from './use-celebration'
import {
  AchievementsContextValue,
  AchievementsProviderProps,
} from './achievements-context-types'
import { AchievementsContext } from './use-achievements'

const LEGACY_STORAGE_KEY_PREFIX = 'quickpass:achievements:'
const PRICE_DROP_FRACTION_FOR_MIC = 0.1

const readLegacyUnlocked = (quickPassUserId: string): AchievementId[] => {
  try {
    const raw = window.localStorage.getItem(
      `${LEGACY_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )
    if (raw === null) {
      return []
    }
    const parsed = JSON.parse(raw) as AchievementId[]
    return Array.isArray(parsed) ? parsed : []
  } catch (error) {
    console.warn('[achievements] failed to read legacy state', error)
    return []
  }
}

const clearLegacyUnlocked = (quickPassUserId: string): void => {
  try {
    window.localStorage.removeItem(
      `${LEGACY_STORAGE_KEY_PREFIX}${quickPassUserId}`,
    )
  } catch (error) {
    console.warn('[achievements] failed to clear legacy state', error)
  }
}

/**
 * Evaluates which achievements the renter has earned and stores the
 * unlocked set on `profile.unlockedAchievementIds` so progress syncs
 * across devices. Fires confetti + a toast the first time a new
 * achievement unlocks. Folds any legacy device-local unlock list
 * forward on first profile load.
 *
 * @param {AchievementsProviderProps} props
 * @returns {JSX.Element}
 */
const AchievementsProvider = ({
  children,
}: AchievementsProviderProps): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { profile, patchProfile } = useQuickPassProfile()
  const { listings } = useImportedListings()
  const { tags, notes } = useListingDecisions()
  const { celebrate } = useCelebration()

  const [recentlyUnlocked, setRecentlyUnlocked] =
    useState<AchievementId | null>(null)
  const hasMigratedReference = useRef<string>('')
  const lastEvaluatedKeyReference = useRef<string>('')

  const unlockedIds = useMemo<AchievementId[]>(() => {
    if (profile === null) {
      return []
    }
    return (profile.unlockedAchievementIds ?? []) as AchievementId[]
  }, [profile])

  useEffect(() => {
    if (quickPassUser === null || profile === null) {
      return
    }

    if (hasMigratedReference.current === quickPassUser.id) {
      return
    }

    hasMigratedReference.current = quickPassUser.id

    const profileEmpty = (profile.unlockedAchievementIds ?? []).length === 0
    const legacy = readLegacyUnlocked(quickPassUser.id)

    if (profileEmpty === true && legacy.length > 0) {
      void patchProfile({ unlockedAchievementIds: legacy })
    }

    if (legacy.length > 0) {
      clearLegacyUnlocked(quickPassUser.id)
    }
  }, [quickPassUser, profile, patchProfile])

  useEffect(() => {
    if (quickPassUser === null || profile === null) {
      return
    }

    const candidates: AchievementId[] = []

    if (profile.onboardingCompletedAt !== null) {
      candidates.push('onboarded')
    }

    if (listings.length >= 1) {
      candidates.push('first-import')
    }

    if (listings.length >= 5) {
      candidates.push('five-listings')
    }

    if (listings.length >= 10) {
      candidates.push('collector')
    }

    const taggedCount = Object.keys(tags).length
    if (taggedCount >= 3) {
      candidates.push('tag-tagger')
    }

    const noteCount = Object.keys(notes).length
    if (noteCount >= 1) {
      candidates.push('note-taker')
    }

    let sawAnyDrop = false
    let sawBigDrop = false

    for (const listing of listings) {
      const validHistory = listing.priceHistory.filter(
        entry => typeof entry.priceMonthly === 'number',
      )
      if (validHistory.length < 2) {
        continue
      }

      const firstPrice = validHistory[0].priceMonthly as number
      const lastPrice = validHistory[validHistory.length - 1]
        .priceMonthly as number

      if (firstPrice > lastPrice) {
        sawAnyDrop = true
        const fraction = (firstPrice - lastPrice) / firstPrice
        if (fraction >= PRICE_DROP_FRACTION_FOR_MIC) {
          sawBigDrop = true
        }
      }
    }

    if (sawAnyDrop === true) {
      candidates.push('price-drop-spotter')
    }

    if (sawBigDrop === true) {
      candidates.push('dropped-the-mic')
    }

    const known = new Set<AchievementId>(unlockedIds)
    let newlyUnlockedId: AchievementId | null = null
    let didChange = false

    for (const candidate of candidates) {
      if (known.has(candidate) === false) {
        known.add(candidate)
        didChange = true
        if (newlyUnlockedId === null) {
          newlyUnlockedId = candidate
        }
      }
    }

    if (didChange === false) {
      return
    }

    const evaluationKey = candidates.join(',')
    if (lastEvaluatedKeyReference.current === evaluationKey) {
      return
    }
    lastEvaluatedKeyReference.current = evaluationKey

    const next = [...known]
    void patchProfile({ unlockedAchievementIds: next })

    if (newlyUnlockedId !== null) {
      setRecentlyUnlocked(newlyUnlockedId)
      celebrate()
    }
  }, [
    quickPassUser,
    profile,
    listings,
    tags,
    notes,
    celebrate,
    patchProfile,
    unlockedIds,
  ])

  const dismissRecentlyUnlocked = useCallback((): void => {
    setRecentlyUnlocked(null)
  }, [])

  const value = useMemo<AchievementsContextValue>(
    () => ({ unlockedIds, recentlyUnlocked, dismissRecentlyUnlocked }),
    [unlockedIds, recentlyUnlocked, dismissRecentlyUnlocked],
  )

  return (
    <AchievementsContext.Provider value={value}>
      {children}
    </AchievementsContext.Provider>
  )
}

export { AchievementsProvider }
